import {useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import type {LocalUpdatePayload, MemexColumnData} from '../../api/columns/contracts/storage'
import type {UpdateMemexItemResponseData} from '../../api/memex-items/contracts'
import type {MemexItemModel} from '../../models/memex-item-model'
import {
  findAllCopiesOfItemInQueryClient,
  setMemexItemsToForceRerenderInQueryClient,
  updateMemexItemInQueryClient,
} from './query-client-api/memex-items'

type ColumnDataType = MemexColumnData | LocalUpdatePayload

type SetMemexItemDataHookReturnType = {
  /**
   * Use to update an item in the local query cache as a result a server request.
   * Creates a new model instance for the updated item which causes components
   * that depend on the model to re-render.
   *
   * If multiple copies of the item exist in the cache for the current view,
   * (e.g., the item exists in multiple groups) all copies will be updated.
   *
   * Generally, avoid using this callback directly and call it from a hook
   * that makes a request to the server to update an item.
   *
   * @param itemUpdate - memex item response data
   */
  setItemData: (itemUpdate: UpdateMemexItemResponseData) => void

  /**
   * Use to optimistically update an item in the local query cache.
   * Mutates the existing model instance for the item which prevents components
   * that only depend on the model from re-rendering. Triggers a rerender by generating
   * an array of items with a new identity, while re-using these existing memex item models.
   *
   * If multiple copies of the item exist in the cache for the current view,
   * (e.g., the item exists in multiple groups) all copies will be updated.
   *
   * @param model - memex item model to update
   * @param columnData - new column data for the item
   */
  mutateColumnDataForItem: (model: MemexItemModel, columnData: ColumnDataType) => void
}

export const useSetMemexItemData = (): SetMemexItemDataHookReturnType => {
  const queryClient = useQueryClient()

  const setItemData = useCallback(
    (itemUpdate: UpdateMemexItemResponseData) => {
      updateMemexItemInQueryClient(queryClient, itemUpdate)
    },
    [queryClient],
  )

  const mutateColumnDataForItem = useCallback(
    (model: MemexItemModel, columnData: ColumnDataType) => {
      // The item may not exist in the cache for the current view yet, so always update the passed in model
      model.setColumnValueForItemColumnType(columnData)
      // Find all copies of the item in the current view
      const items = findAllCopiesOfItemInQueryClient(queryClient, model.id)
      for (const item of items) {
        if (item === model) continue // already updated this instance above
        item.setColumnValueForItemColumnType(columnData)
      }
      if (items.length > 0) {
        // Force a re-render without recreating new item model instances
        setMemexItemsToForceRerenderInQueryClient(queryClient)
      }
    },
    [queryClient],
  )

  return {setItemData, mutateColumnDataForItem}
}
